import {
  decrypt,
  encrypt,
  type Keycheck,
  makeKeycheck,
  unlockKeycheck,
} from './crypto';
import type { GitHub } from './github';

const KEYCHECK_PATH = '.notes-keycheck.json';

export type UnlockResult =
  | { ok: true, key: CryptoKey, keycheck: Keycheck }
  | { ok: false, reason: 'wrong-passphrase' };

// The remote keycheck when it can be read, the cached one when offline, with
// why the remote couldn't be. The remote always wins: it is what a changed
// passphrase or a newly set up authenticator replaces.
const latestKeycheck = (github: GitHub, cached: Keycheck | null) =>
  remoteKeycheck(github).then(
    remote => ({ keycheck: remote ?? cached, unreachable: null }),
    (e: unknown) => {
      if (!cached) throw e;
      return { keycheck: cached, unreachable: e };
    },
  );

// Whether a key opened from the cached keycheck still matches the remote's,
// for the app to open at once on a remembered key rather than wait on
// GitHub. Asked before every sync until a check gets through, so nothing
// goes out under a passphrase another device has since replaced; once it
// has, the answer stands. A remote with no keycheck has replaced nothing.
export const keycheckGate = (
  github: GitHub,
  opened: Keycheck,
  // the remote one, kept for the next unlock - an authenticator set up since
  save: (keycheck: Keycheck) => Promise<void>,
) => {
  let confirmed = false;
  return {
    confirmed: () => confirmed,
    check: async () => {
      if (confirmed) return true;
      const remote = await remoteKeycheck(github);
      if (remote && remote.check !== opened.check) return false;
      if (remote) await save(remote);
      confirmed = true;
      return true;
    },
  };
};

export type KeycheckGate = ReturnType<typeof keycheckGate>;

// The first device to unlock a repo sets its passphrase; every later one has
// to match it. The keycheck is cached locally after each unlock so the app
// opens offline.
export async function unlock(
  github: GitHub,
  passphrase: string,
  cached: Keycheck | null,
): Promise<UnlockResult> {
  const { keycheck, unreachable } = await latestKeycheck(github, cached);
  if (!keycheck) {
    await github.ensureBranch();
    const created = await makeKeycheck(passphrase);
    await github.write(
      KEYCHECK_PATH,
      JSON.stringify(created.keycheck, null, 2),
      null,
      'set up notes',
    );
    return { ok: true, ...created };
  }
  const key = await unlockKeycheck(passphrase, keycheck);
  if (key) return { ok: true, key, keycheck };
  // checked only against the cached keycheck, which a passphrase changed on
  // another device leaves behind: the right one would read as wrong
  if (unreachable) {
    throw new Error(
      'Wrong passphrase, or it was changed on another device and GitHub '
        + `couldn't be reached to check: ${unreachable}`,
      { cause: unreachable },
    );
  }
  return { ok: false, reason: 'wrong-passphrase' };
}

// null until an authenticator has been set up for the repo
export const totpSecret = (key: CryptoKey, keycheck: Keycheck) =>
  keycheck.totp ? decrypt(key, keycheck.totp) : Promise.resolve(null);

// Stores the authenticator secret next to the passphrase check, for every
// device to find. Returns the keycheck to cache.
export async function enrollTotp(
  github: GitHub,
  key: CryptoKey,
  keycheck: Keycheck,
  secret: string,
): Promise<Keycheck> {
  const next: Keycheck = { ...keycheck, totp: await encrypt(key, secret) };
  const current = await github.read(KEYCHECK_PATH);
  await github.write(
    KEYCHECK_PATH,
    JSON.stringify(next, null, 2),
    current?.sha ?? null,
    'set up authenticator',
  );
  return next;
}

const remoteKeycheck = async (github: GitHub) => {
  const file = await github.read(KEYCHECK_PATH);
  return file ? JSON.parse(file.text) as Keycheck : null;
};
